import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'

export type PlanItemStatus = 'Open' | 'Complete' | 'Skipped'
export type Urgency = 'None' | 'AtRisk' | 'Overdue'

export interface Progress {
  total: number
  complete: number
  skipped: number
  resolved: number
  open: number
  overdue: number
  atRisk: number
  /** Null for a group with no items: nothing here is not the same as nothing done. */
  percentComplete: number | null
  isAtRisk: boolean
}

export interface PlanMilestone {
  id: string
  title: string
  description: string | null
  phase: string
  offsetDays: number
  anchor: string
  ownerRole: string | null
  dueDate: string | null
  isDueDateOverridden: boolean
  isAdHoc: boolean
  status: PlanItemStatus
  urgency: Urgency
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  completedAt: string | null
  /** Who resolved it - for a completion or a skip alike. */
  completedByUserId: string | null
  completedByDisplayName: string | null
  skipReason: string | null
}

export interface PlanChecklistItem {
  id: string
  sectionTitle: string
  title: string
  description: string | null
  phase: string
  ownerRole: string | null
  requiresNote: boolean
  requiresAttachment: boolean
  dueDate: string | null
  isAdHoc: boolean
  status: PlanItemStatus
  urgency: Urgency
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  completedAt: string | null
  /** Who resolved it - for a completion or a skip alike. */
  completedByUserId: string | null
  completedByDisplayName: string | null
  skipReason: string | null
}

export interface PlanPhase {
  phase: string
  phaseDisplayName: string
  progress: Progress
  milestones: PlanMilestone[]
  checklistItems: PlanChecklistItem[]
}

export interface PlanSource {
  name: string
  version: number
}

export interface Plan {
  id: string
  eventId: string
  eventName: string
  eventStartDate: string
  eventEndDate: string
  timelineSource: PlanSource
  checklistSources: PlanSource[]
  progress: Progress
  /** "Today" in the event's own time zone, which overdue is judged against. */
  evaluatedOn: string
  phases: PlanPhase[]
  createdAt: string
}

export interface DueDateChangeRow {
  milestoneId: string
  title: string
  phase: string
  currentDueDate: string | null
  proposedDueDate: string | null
  willMove: boolean
  skippedReason: string | null
}

export interface DateChangePreview {
  currentStartDate: string
  currentEndDate: string
  proposedStartDate: string
  proposedEndDate: string
  willMoveCount: number
  unchangedCount: number
  milestones: DueDateChangeRow[]
}

export interface PlanDeletionPreview {
  planId: string
  milestoneCount: number
  checklistItemCount: number
  completedCount: number
  attachmentCount: number
  noteCount: number
}

const planKey = (eventId: string) => ['plan', eventId] as const

export function usePlan(eventId: string | undefined) {
  return useQuery({
    queryKey: planKey(eventId ?? ''),
    queryFn: () => apiFetch<Plan>(`/api/events/${eventId}/plan`),
    enabled: Boolean(eventId),
    // A missing plan is a normal state for a new event, not a transient error.
    retry: false,
  })
}

export function useCreatePlan(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: { timelineTemplateId: string; checklistTemplateIds: string[] }) =>
      apiFetch<{ id: string }>(`/api/events/${eventId}/plan`, { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planKey(eventId) })
      void queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

/**
 * Adds a published checklist template's items to a plan that already exists.
 *
 * The counterpart to composing without one: a timeline-only plan would
 * otherwise be a one-way door, since the alternatives are ad-hoc items one at
 * a time or deleting the plan and losing every completion on it.
 */
export function useAttachChecklist(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: { checklistTemplateId: string }) =>
      apiFetch<{ id: string }>(`/api/events/${eventId}/plan/checklists`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planKey(eventId) })
      void queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

export function usePreviewDateChange(eventId: string) {
  return useMutation({
    mutationFn: (body: { startDate: string; endDate: string }) =>
      apiFetch<DateChangePreview>(`/api/events/${eventId}/plan/date-change-preview`, {
        method: 'POST',
        body,
      }),
  })
}

export function usePlanDeletionPreview(eventId: string) {
  return useMutation({
    mutationFn: () => apiFetch<PlanDeletionPreview>(`/api/events/${eventId}/plan/deletion-preview`),
  })
}

export function useDeletePlan(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/events/${eventId}/plan?confirm=true`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planKey(eventId) })
      void queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

/** Formats a plain calendar date, parsed as local midnight to avoid an off-by-one. */
export function formatDate(date: string | null): string {
  if (!date) return '—'
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
